import classNames from 'classnames'
import _ from 'lodash'
import moment, { Moment } from 'moment-timezone'
import React from 'react'
import { DayPicker, type Matcher } from 'react-day-picker-v9'
import 'react-day-picker-v9/style.css'
import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import Select from 'react-select'

import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { TimeWheelPicker, Period } from 'browser/components/atomic-elements/atoms/input/time-wheel/time-wheel-picker'
import { getTimezoneOptions } from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'
import 'browser/components/atomic-elements/atoms/date-picker/_date-picker.scss'
import './_date-time-picker-popover.scss'

const timezoneSelectStyles = {
  control: (base: any) => ({
    ...base,
    border: 'none',
    borderTop: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 0,
    boxShadow: 'none',
    background: 'transparent',
    minHeight: 32,
    cursor: 'pointer',
    ':hover': { borderTop: '1px solid rgba(0,0,0,0.08)' },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '0 4px',
    fontSize: 12,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: 'inherit',
  }),
  placeholder: (base: any) => ({
    ...base,
    fontSize: 12,
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: 12,
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    padding: 0,
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: '0 4px',
    color: 'rgba(0,0,0,0.35)',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base: any) => ({
    ...base,
    margin: 0,
    borderRadius: '4px 4px 0 0',
    boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
    // Expand to fit content width and anchor to the right edge of the control
    // so the menu grows leftward, overlaying the calendar in datetime mode
    // rather than overflowing off-screen to the right.
    width: 'max-content',
    minWidth: '100%',
    // Viewport-capped so the right-anchored menu can't grow leftward past the
    // edge of a narrow mobile-web sheet. Long zone labels truncate instead
    // (menuList clips overflow-x).
    maxWidth: 'min(360px, 90vw)',
    right: 0,
    left: 'auto',
    zIndex: 10,
  }),
  menuList: (base: any) => ({
    ...base,
    overflowX: 'hidden',
    whiteSpace: 'nowrap',
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 12,
    whiteSpace: 'nowrap',
    backgroundColor: state.isSelected
      ? 'rgba(0,0,0,0.08)'
      : state.isFocused
        ? 'rgba(0,0,0,0.04)'
        : 'transparent',
    color: 'inherit',
    cursor: 'pointer',
  }),
}

function derivePendingState(
  value: any,
  defaultTimezone: string,
  minuteStep: number,
): IDateTimePickerPopoverState {
  const dateTime = _.get(value, 'dateTime', value)
  const timezone = _.get(value, 'timezone', defaultTimezone) || defaultTimezone

  let pendingDate: Moment | null = null
  let pendingHour: number | null = null
  let pendingMinute: number | null = null
  let pendingPeriod: Period | null = null

  if (dateTime) {
    const m = moment.tz(dateTime, moment.ISO_8601, true, timezone)
    if (m.isValid()) {
      pendingDate = m
      const hours24 = m.hours()
      pendingPeriod = hours24 < 12 ? 'AM' : 'PM'
      pendingHour = hours24 % 12 === 0 ? 12 : hours24 % 12
      pendingMinute = Math.round(m.minutes() / minuteStep) * minuteStep % 60
    }
  }

  return {
    pendingDate,
    pendingHour,
    pendingMinute,
    pendingPeriod,
    pendingTimezone: timezone,
    visibleMonth: (pendingDate ?? moment.tz(timezone)).toDate(),
  }
}

export interface IDateTimePickerPopoverProps {
  value: any
  onChange: (value: any) => void
  mode?: 'date' | 'time' | 'datetime'
  /**
   * When set to 'datetime' alongside mode='date' or mode='time', the component
   * preserves the untouched half of the value on apply (e.g. keeps the existing
   * time when only the date is being edited, and vice-versa). This is the web
   * equivalent of the same prop on the mobile FormDateTime.
   */
  valueMode?: 'date' | 'time' | 'datetime'
  showTimezoneInput?: boolean
  minDate?: string
  maxDate?: string
  daySize?: number
  defaultTimezone?: string
  minuteStep?: number
  /** Injected by TetherTarget via cloneElement */
  onClose?: () => void
  /**
   * When true, don't render the in-popover Apply button. The caller (e.g.
   * the mobile-web bottom sheet) supplies its own apply affordance and
   * invokes the picker's `apply()` method via ref.
   */
  hideFooter?: boolean
  /**
   * Drops the popover's own border / background / box-shadow so it integrates
   * cleanly inside another surface (sheet, modal). Use alongside `hideFooter`.
   */
  inline?: boolean
  /**
   * Fires whenever the picker's "ready to apply" state changes — used by
   * external apply affordances to drive their enabled/disabled state.
   */
  onCanApplyChange?: (canApply: boolean) => void
}

interface IDateTimePickerPopoverState {
  pendingDate: Moment | null
  pendingHour: number | null
  pendingMinute: number | null
  pendingPeriod: Period | null
  pendingTimezone: string
  /** Currently visible calendar month — bound for navigation. */
  visibleMonth: Date
}

/**
 * Combined date + time picker rendered inside a TetherTarget popover.
 * Supports date-only, time-only, and datetime modes.
 * The value is only committed when the user clicks Apply (or presses Enter).
 */
export class DateTimePickerPopover extends React.Component<
  IDateTimePickerPopoverProps,
  IDateTimePickerPopoverState
> {
  private today: Moment

  public static defaultProps: Partial<IDateTimePickerPopoverProps> = {
    daySize: 30,
    defaultTimezone: moment.tz.guess(),
    minuteStep: 5,
    mode: 'datetime',
  }

  constructor(props: IDateTimePickerPopoverProps) {
    super(props)
    this.today = moment.tz(props.defaultTimezone)
    this.state = derivePendingState(props.value, props.defaultTimezone, props.minuteStep ?? 5)
  }

  public componentDidMount() {
    this.props.onCanApplyChange?.(this.computeCanApply())
  }

  public componentDidUpdate(prevProps: IDateTimePickerPopoverProps, prevState: IDateTimePickerPopoverState) {
    const prev = this.computeCanApply(prevState)
    const next = this.computeCanApply()
    if (prev !== next) {
      this.props.onCanApplyChange?.(next)
    }
  }

  /** Public method — lets external apply affordances (e.g. sheet header) trigger commit. */
  public apply = (): void => {
    if (this.computeCanApply()) this.handleApply()
  }

  private computeCanApply(state: IDateTimePickerPopoverState = this.state): boolean {
    const { mode = 'datetime' } = this.props
    const showCalendar = mode !== 'time'
    const showTime = mode !== 'date'
    return (
      (!showCalendar || state.pendingDate != null) &&
      (!showTime || (state.pendingHour != null && state.pendingMinute != null && state.pendingPeriod != null))
    )
  }

  public render() {
    const { showTimezoneInput, mode = 'datetime', minuteStep = 5, hideFooter, inline, minDate, maxDate } = this.props
    const { pendingDate, pendingHour, pendingMinute, pendingPeriod, pendingTimezone, visibleMonth } = this.state

    const showCalendar = mode !== 'time'
    const showTime = mode !== 'date'

    const canApply = this.computeCanApply()

    const disabledMatcher = this.getDisabledMatcher()
    const startMonth = minDate ? new Date(minDate) : new Date(1900, 0, 1)
    const endMonth = maxDate ? new Date(maxDate) : new Date(2100, 11, 31)

    return (
      <div
        className={classNames(`c-dtPicker c-dtPicker--${mode}`, { 'c-dtPicker--inline': inline })}
        onKeyDown={this.handleKeyDown}
      >
        <div className='c-dtPicker-body'>
          {showCalendar && (
            <div className='c-dtPicker-calendar'>
              <DayPicker
                mode='single'
                selected={pendingDate?.toDate()}
                onSelect={this.handleDaySelect}
                month={visibleMonth}
                onMonthChange={this.handleMonthChange}
                disabled={disabledMatcher}
                captionLayout='dropdown'
                startMonth={startMonth}
                endMonth={endMonth}
                showOutsideDays={false}
                components={{ Chevron: ChevronIcon }}
              />
            </div>
          )}
          {showTime && (
            <div className='c-dtPicker-timeSection'>
              <TimeWheelPicker
                hour={pendingHour}
                minute={pendingMinute}
                period={pendingPeriod}
                minuteStep={minuteStep}
                onChange={this.handleTimeChange}
              />
              {showTimezoneInput && (
                <Select
                  className='c-dtPicker-timezoneSelect'
                  classNamePrefix='c-dtPicker-tz'
                  options={getTimezoneOptions()}
                  value={getTimezoneOptions().find((o) => o.value === pendingTimezone) ?? null}
                  onChange={(opt) => opt && this.handleTimezoneChange(opt.value)}
                  isSearchable={true}
                  menuPlacement='top'
                  menuPortalTarget={null}
                  styles={timezoneSelectStyles}
                  placeholder='Select timezone…'
                />
              )}
            </div>
          )}
        </div>
        {!hideFooter && (
          <div className='c-dtPicker-footer'>
            <Button
              intent='primary'
              buttonText='Apply'
              isDisabled={!canApply}
              onClick={this.handleApply}
            />
          </div>
        )}
      </div>
    )
  }

  private handleDaySelect = (date: Date | undefined) => {
    if (!date) {
      this.setState({ pendingDate: null })
      return
    }
    // Preserve the existing pending timezone so the moment we store reflects
    // the picker's own zone rather than the local browser zone.
    const m = moment.tz(
      { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() },
      this.state.pendingTimezone,
    )
    this.setState({ pendingDate: m, visibleMonth: date })
  }

  private handleMonthChange = (date: Date) => {
    this.setState({ visibleMonth: date })
  }

  private handleTimeChange = (hour: number, minute: number, period: Period) => {
    this.setState({ pendingHour: hour, pendingMinute: minute, pendingPeriod: period })
  }

  private handleTimezoneChange = (timezone: string) => {
    this.setState({ pendingTimezone: timezone })
  }

  private handleApply = () => {
    const { onChange, showTimezoneInput, onClose, mode = 'datetime', valueMode } = this.props
    const { pendingDate, pendingHour, pendingMinute, pendingPeriod, pendingTimezone } = this.state

    let result: Moment

    if (mode === 'date') {
      if (!pendingDate) { return }
      result = pendingDate.clone()
      if (valueMode === 'datetime') {
        // Preserve the existing time so we don't clobber the time half when this
        // field shares a datetime property with a separate time field.
        const existing = this.getExistingMoment(pendingTimezone)
        if (existing) {
          result.hours(existing.hours()).minutes(existing.minutes())
            .seconds(existing.seconds()).milliseconds(existing.milliseconds())
        } else {
          result.startOf('day')
        }
      } else {
        result.startOf('day')
      }
    } else if (mode === 'time') {
      if (pendingHour == null || pendingMinute == null || !pendingPeriod) { return }
      let hours24 = pendingHour % 12
      if (pendingPeriod === 'PM') { hours24 += 12 }
      if (valueMode === 'datetime') {
        // Preserve the existing date so we don't clobber the date half when this
        // field shares a datetime property with a separate date field.
        const existing = this.getExistingMoment(pendingTimezone)
        result = existing ? existing.clone() : moment.tz(pendingTimezone).startOf('day')
      } else {
        result = moment.tz(pendingTimezone).startOf('day')
      }
      result.hours(hours24).minutes(pendingMinute).seconds(0).milliseconds(0)
    } else {
      if (!pendingDate || pendingHour == null || pendingMinute == null || !pendingPeriod) { return }
      result = pendingDate.clone()
      let hours24 = pendingHour % 12
      if (pendingPeriod === 'PM') { hours24 += 12 }
      result.hours(hours24).minutes(pendingMinute).seconds(0).milliseconds(0)
    }

    if (showTimezoneInput) {
      onChange({ dateTime: result.toISOString(), timezone: pendingTimezone })
    } else {
      onChange(result.toISOString())
    }

    if (onClose) { onClose() }
  }

  /** Parses the current prop value into a moment, used for valueMode preservation. */
  private getExistingMoment(timezone: string): Moment | null {
    const raw = _.get(this.props.value, 'dateTime', this.props.value)
    if (!raw) { return null }
    const m = moment.tz(raw, moment.ISO_8601, true, timezone)
    return m.isValid() ? m : null
  }

  private handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') { return }
    event.preventDefault()
    this.handleApply()
  }

  // react-day-picker's `disabled` accepts a single matcher or an array. We
  // build a list from minDate/maxDate; selected + today + hover styling are
  // all handled natively by the library (selected = the `selected` prop,
  // today = built-in modifier, hover = native :hover state on the day cell).
  private getDisabledMatcher(): Matcher[] | undefined {
    const { minDate, maxDate } = this.props
    const matchers: Matcher[] = []
    if (!_.isNil(minDate)) matchers.push({ before: moment(minDate).startOf('day').toDate() })
    if (!_.isNil(maxDate)) matchers.push({ after: moment(maxDate).endOf('day').toDate() })
    return matchers.length > 0 ? matchers : undefined
  }
}

// Single chevron component used by react-day-picker for prev / next nav and
// for the month / year dropdown indicators. Orientation tells us which icon
// to show.
const ChevronIcon: React.FC<{ orientation?: 'left' | 'right' | 'up' | 'down' }> = ({ orientation }) => {
  const icon =
    orientation === 'left' ? IconNames.CHEVRON_LEFT
    : orientation === 'right' ? IconNames.CHEVRON_RIGHT
    : orientation === 'up' ? IconNames.CHEVRON_UP
    : IconNames.CHEVRON_DOWN
  return <Icon icon={icon} />
}
